import {Box, Button, Grid, Stack, Typography, useTheme, Link} from '@mui/material';
import {setOnboarding, OnboardingActions} from 'app/store/appState/appStateSlice';
import {selectUserType} from 'app/store/users/userSlice';
import {LogInType} from 'app/types/Shared';
import {useFeatureFlag} from 'configcat-react';
import {useDispatch, useSelector} from 'react-redux';

const WelcomeContent: Array<{title: string; text: string}> = [
  {title: 'Visualize', text: 'end-to-end processes across study lifecycle'},
  {title: 'Navigate', text: 'each process step with clearly defined roles and responsibilities'},
  {title: 'Access', text: 'tools and resources right when you need them'},
];

const linksExternal: Array<{title: string; url: string}> = [
  {
    title: 'View the Quick Start Guide',
    url: 'https://mytakeda.sharepoint.com/:b:/r/sites/ELEVATEBCDR/Shared%20Documents/ELEVATE%20Basics/Guides/Navigating%20ELEVATE%20for%20CROs.pdf?csf=1&web=1&e=j4b2B2',
  },
];

const linksInternal: Array<{title: string; url: string}> = [
  {
    title: 'View the Quick Start Guide',
    url: 'https://mytakeda.sharepoint.com/:b:/r/sites/ELEVATEBCDR/Shared%20Documents/ELEVATE%20Basics/Guides/Navigating%20ELEVATE.pdf?csf=1&web=1&e=ZgBVFB',
  },
];

const WelcomeScreen = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const userType = useSelector(selectUserType);
  const {value: isWelcomeVideoEnabled} = useFeatureFlag('content_welcome_video', false);

  if (isWelcomeVideoEnabled && linksInternal?.length === 1 && linksExternal?.length === 1) {
    linksInternal.unshift({
      title: 'Watch a 2-minute Demo',
      url: 'https://mytakeda.sharepoint.com/:v:/r/sites/ELEVATEBCDR/Shared%20Documents/ELEVATE%20Basics/Demo%20Video/Navigating%20ELEVATE.mp4?csf=1&web=1&e=vJdfRt',
    });
    linksExternal.unshift({
      title: 'Watch a 2-minute Demo',
      url: 'https://mytakeda.sharepoint.com/:v:/r/sites/ELEVATEBCDR/Shared%20Documents/ELEVATE%20Basics/Demo%20Video/Navigating%20ELEVATE%20for%20CROs.mp4?csf=1&web=1&e=3WBxJb',
    });
  }

  const handleButtonClick = () => {
    dispatch(setOnboarding(OnboardingActions.DisableOnboardingModal));
  };

  const links = userType === LogInType.External ? linksExternal : linksInternal;

  return (
    <Box
      sx={{
        background: theme.palette.grey[100],
        borderRadius: '12px',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <Stack
        sx={{
          width: '100%',
          maxWidth: '1200px',
          p: 24,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box width='100%' textAlign='center' py={6}>
          <Typography variant='h1' color={theme.palette.primary.main}>
            Welcome to <b>ELEVATE {userType === LogInType.External && 'for CROs'}</b>
          </Typography>
        </Box>
        <Grid
          container
          direction='row'
          maxWidth={'780px'}
          spacing={1}
          px={4}
          border={`1px solid ${theme.palette.grey[500]}`}
          bgcolor='white'
        >
          {WelcomeContent.map((content, index) => (
            <Grid
              minHeight={'140px'}
              item
              md={12 / WelcomeContent.length}
              xs={12}
              key={content.title}
              display='flex'
              justifyContent='center'
              alignItems='flex-start'
            >
              <Stack spacing={1} px={2} py={2} textAlign='center'>
                <Typography variant='h1' fontWeight={600}>
                  {content.title}
                </Typography>
                <Box
                  width='100%'
                  maxWidth='300px'
                  px={2}
                  textAlign='center'
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                >
                  <Typography variant='h5' fontWeight={400}>
                    {content.text}
                  </Typography>
                </Box>
              </Stack>
              {index !== WelcomeContent.length - 1 && (
                <Box
                  width='5px'
                  height='100%'
                  //border='1px solid blue'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  <Box width='5px' height='30%' borderRight={`1px solid ${theme.palette.grey[500]}`} />
                </Box>
              )}
            </Grid>
          ))}
        </Grid>

        <Stack width='100%' spacing={2} justifyContent='center' alignItems='center' pt={6} minHeight={160}>
          <Button sx={{bgcolor: theme.palette.primary.main}} variant='contained' onClick={() => handleButtonClick()}>
            Get Started
          </Button>
          {links.map((link) => (
            <Link
              underline='hover'
              sx={{cursor: 'pointer', color: theme.palette.primary.main}}
              display='flex'
              alignItems='flex-end'
              onClick={() => window.open(link.url)}
              key={link.title}
            >
              {link.title}
            </Link>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
export default WelcomeScreen;
